import React from 'react';
import {StaticDatePicker as MuiDatePicker} from '@mui/x-date-pickers/StaticDatePicker';
import MuiIcon from '@mui/material/Icon';
import {DatePickersLocalizationProvider} from '@famly/mf_date-and-time_date-pickers-localizations';
import {useDataProps} from '@famly/mf_ts-utils_data-aria-props';

import {type DatePickerProps, DayRenderer} from './date-picker';
import {dateViewsOrder} from './date-views-order';

export type StandaloneDatePickerProps = Pick<
    DatePickerProps,
    | 'autoFocus'
    | 'defaultCalendarMonth'
    | 'defaultValue'
    | 'disabled'
    | 'disableFuture'
    | 'disablePast'
    | 'maxDate'
    | 'minDate'
    | 'onChange'
    | 'onMonthChange'
    | 'onYearChange'
    | 'shouldDisableDate'
    | 'shouldDisableMonth'
    | 'shouldDisableYear'
    | 'value'
> &
    DataProps;

export const StandaloneDatePicker = ({
    autoFocus,
    defaultCalendarMonth,
    defaultValue,
    disabled,
    disableFuture,
    disablePast,
    maxDate,
    minDate,
    onChange,
    onMonthChange,
    onYearChange,
    shouldDisableDate,
    shouldDisableMonth,
    shouldDisableYear,
    value,
    ...rest
}: StandaloneDatePickerProps) => {
    const dataProps = useDataProps(rest);

    return (
        <DatePickersLocalizationProvider>
            <div {...dataProps}>
                <MuiDatePicker
                    views={dateViewsOrder}
                    autoFocus={autoFocus}
                    defaultCalendarMonth={defaultCalendarMonth}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    disableFuture={disableFuture}
                    disablePast={disablePast}
                    fixedWeekNumber={6}
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={(...args) => {
                        onChange?.(...args);
                    }}
                    onMonthChange={onMonthChange}
                    onYearChange={onYearChange}
                    shouldDisableDate={shouldDisableDate}
                    shouldDisableMonth={shouldDisableMonth}
                    shouldDisableYear={shouldDisableYear}
                    showDaysOutsideCurrentMonth={true}
                    value={value}
                    slots={{
                        day: DayRenderer,
                        switchViewIcon: props => {
                            return (
                                <MuiIcon
                                    {...props}
                                    sx={theme => ({
                                        color: theme.modernFamlyTheme.colorPalette.n400,
                                        // The following ensures that MUI's default rotation transition is kept intact
                                        transitionProperty: 'transform',
                                    })}
                                >
                                    expand_more
                                </MuiIcon>
                            );
                        },

                        // Disables the date picker from showing a large headline with the selected date on mobile
                        toolbar: () => null,

                        actionBar: () => null,
                    }}
                    {...dataProps}
                />
            </div>
        </DatePickersLocalizationProvider>
    );
};
